import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { exchangeKeys } from './hooks';
import {
  ensureProjectFolder,
  shareFileWithLink,
  uploadFileToDrive,
  withDriveToken,
} from '../files/drive';
import { patchCachedRows, restoreCachedRows } from '../../lib/optimistic';
import type { SupabaseClient } from '../../lib/supabase';

/** Same folder the legacy sample photos use, so a supplier has one place. */
const SAMPLE_OPTION_FOLDER = 'TAP Sample Photos';

/**
 * Sample review: components, options, verdicts and the internal client tag.
 *
 * The verdict columns are deliberately not sent on update. A trigger stamps
 * decided_by and decided_at from auth.uid(), so one reviewer's decision can
 * never be recorded under another's name — sending them from here would be
 * asking the client to be honest about who clicked.
 */

export type OptionVerdict = 'pending' | 'approved' | 'denied';

export interface SampleComponentType {
  id: string;
  sort_order: number;
  is_active: boolean;
}

export interface SubmissionOption {
  id: string;
  submission_id: string;
  component_type: string;
  round: number;
  label: string;
  url: string;
  verdict: OptionVerdict;
  feedback: string;
  decided_by: string | null;
  decided_at: string | null;
  /**
   * When the factory SENT this option, as part of a batch (20261119000200).
   * Null means they are still assembling the round: they may add more, and
   * TAP may not rule on it yet. Uploading and submitting used to be the same
   * event, which is why a component closed after one photo.
   */
  submitted_at: string | null;
  created_at: string;
}

export interface TaggedClient {
  submission_id: string;
  company_id: string;
  company_name: string;
}

const OPTION_COLUMNS =
  'id, submission_id, component_type, round, label, url, verdict, feedback, decided_by, decided_at, submitted_at, created_at';

/** The submission fields a CRM reader needs; no supplier identity beyond it. */
// prettier-ignore
const COMPANY_SUBMISSION_COLUMNS = 'id, title, status, created_at, decided_at, decision_note';

export interface CompanySubmission {
  id: string;
  title: string;
  status: string;
  created_at: string;
  decided_at: string | null;
  decision_note: string;
}

export const sampleKeys = {
  all: ['sample-review'] as const,
  components: () => [...sampleKeys.all, 'components'] as const,
  options: (submissionId: string) => [...sampleKeys.all, 'options', submissionId] as const,
  /**
   * One key for a whole page of submissions. The ids are sorted into the key
   * so that two renders holding the same set in a different order share a
   * cache entry instead of refetching each other's work.
   */
  optionCounts: (submissionIds: string[]) =>
    [...sampleKeys.all, 'option-counts', [...submissionIds].sort().join(',')] as const,
  clients: () => [...sampleKeys.all, 'clients'] as const,
  companySubmissions: (companyId: string) =>
    [...sampleKeys.all, 'company-submissions', companyId] as const,
};

export async function fetchComponentTypes(client: SupabaseClient): Promise<SampleComponentType[]> {
  const { data, error } = await client
    .from('sample_component_types')
    .select('id, sort_order, is_active')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchSubmissionOptions(
  client: SupabaseClient,
  submissionId: string,
): Promise<SubmissionOption[]> {
  const { data, error } = await client
    .from('submission_options')
    .select(OPTION_COLUMNS)
    .eq('submission_id', submissionId)
    .order('round', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) {
    throw new Error(error.message);
  }
  // verdict is a text column with a CHECK, so it arrives as string. Narrowed
  // here rather than asserted, so an unexpected value becomes 'pending' —
  // visibly undecided — instead of being trusted into the union.
  return (data ?? []).map((row) => ({ ...row, verdict: asVerdict(row.verdict) }));
}

function asVerdict(raw: string): OptionVerdict {
  return raw === 'approved' || raw === 'denied' ? raw : 'pending';
}

/**
 * How many photos each submission holds.
 *
 * The collapsed row in the supplier feed prints a photo count, and the whole
 * point of collapsing is that the four component panels are NOT mounted — so
 * the per-submission options query that would normally answer this never runs.
 * Asking once for the visible page is the difference between one request and
 * thirty.
 */
export function tallyOptionCounts(rows: { submission_id: string }[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    counts.set(row.submission_id, (counts.get(row.submission_id) ?? 0) + 1);
  }
  return counts;
}

export async function fetchOptionCounts(
  client: SupabaseClient,
  submissionIds: string[],
): Promise<Map<string, number>> {
  if (submissionIds.length === 0) {
    return new Map();
  }
  // submission_id only. Row Level Security already scopes this to the reader's
  // own organisation, and pulling urls and feedback to count them would ship a
  // page of data to produce a page of integers.
  const { data, error } = await client
    .from('submission_options')
    .select('submission_id')
    .in('submission_id', submissionIds);
  if (error) {
    throw new Error(error.message);
  }
  return tallyOptionCounts(data ?? []);
}

export function useSubmissionOptionCounts(submissionIds: string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: sampleKeys.optionCounts(submissionIds),
    queryFn: () => fetchOptionCounts(client, submissionIds),
    enabled: submissionIds.length > 0,
    retry: false,
  });
}

/**
 * Client names for tagged submissions.
 *
 * Through the function rather than the table: submission_internal_tags holds a
 * company_id, and public.companies stays gated to the sales team, so a
 * reviewer reading the table directly would get a UUID they cannot resolve.
 */
export async function fetchTaggedClients(client: SupabaseClient): Promise<TaggedClient[]> {
  const { data, error } = await client.rpc('tagged_client_names');
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

/**
 * Every sample run tagged for one company, newest first.
 *
 * Two reads rather than a join, because the two halves are gated differently.
 * tagged_client_names() is the only door onto the tag for a non-sales
 * reviewer — public.companies stays sales-only — and supplier_submissions is
 * readable by any TAP user. Joining them in SQL would need a new function
 * that re-derives both gates; joining them here needs neither, and a supplier
 * calling this gets an empty list from the RPC and stops there.
 */
export async function fetchCompanySubmissions(
  client: SupabaseClient,
  companyId: string,
): Promise<CompanySubmission[]> {
  const { data: tags, error: tagError } = await client.rpc('tagged_client_names');
  if (tagError) {
    throw new Error(tagError.message);
  }
  const ids = ((tags ?? []) as TaggedClient[])
    .filter((tag) => tag.company_id === companyId)
    .map((tag) => tag.submission_id);
  if (ids.length === 0) {
    return [];
  }

  const { data, error } = await client
    .from('supplier_submissions')
    .select(COMPANY_SUBMISSION_COLUMNS)
    .in('id', ids)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export function useCompanySubmissions(companyId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: sampleKeys.companySubmissions(companyId ?? ''),
    queryFn: () => fetchCompanySubmissions(client, companyId ?? ''),
    enabled: companyId !== null,
    retry: false,
  });
}

export function useComponentTypes() {
  const { client } = useAuth();
  return useQuery({
    queryKey: sampleKeys.components(),
    queryFn: () => fetchComponentTypes(client),
    retry: false,
  });
}

export function useSubmissionOptions(submissionId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: sampleKeys.options(submissionId ?? ''),
    queryFn: () => fetchSubmissionOptions(client, submissionId as string),
    enabled: submissionId !== null,
    retry: false,
  });
}

export function useTaggedClients() {
  const { client } = useAuth();
  return useQuery({
    queryKey: sampleKeys.clients(),
    queryFn: () => fetchTaggedClients(client),
    retry: false,
  });
}

/**
 * Judging one option.
 *
 * Optimistic, like the boards: a reviewer working through a dozen cards should
 * not wait on a round trip for each. The rollback matters more than usual here
 * — approving a second option in a slot is refused by a unique index, and the
 * card has to go back rather than sit there looking approved.
 */
export function useReviewOption(submissionId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { optionId: string; verdict: OptionVerdict; feedback?: string }) => {
      const changes: { verdict: OptionVerdict; feedback?: string } = { verdict: input.verdict };
      if (input.feedback !== undefined) {
        changes.feedback = input.feedback;
      }
      const { error } = await client
        .from('submission_options')
        .update(changes)
        .eq('id', input.optionId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onMutate: (input) =>
      patchCachedRows<SubmissionOption>(queryClient, sampleKeys.options(submissionId), [
        {
          id: input.optionId,
          changes:
            input.feedback === undefined
              ? { verdict: input.verdict }
              : { verdict: input.verdict, feedback: input.feedback },
        },
      ]),
    onError: (_error, _input, context) => {
      restoreCachedRows(queryClient, sampleKeys.options(submissionId), context);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sampleKeys.options(submissionId) });
    },
  });
}

/**
 * A supplier sending one option for one component.
 *
 * The round is NOT sent. guard_option_insert derives it: the current round
 * stays open until every option in it has been denied, and only then does the
 * next one begin. Two tabs that each guessed a number would disagree, and the
 * five-per-round cap counts on that number being right.
 *
 * The photo goes to the supplier's own Drive, exactly as the legacy sample
 * photo upload does — never the shared TAP root, which a supplier must not
 * reach.
 */
/**
 * The upload itself, as a plain function.
 *
 * Pulled out of the hook below because the New Submission dialog cannot use a
 * hook here: it does not know the submission id until the moment it creates the
 * row, and a hook parameterised at render time would still be holding the empty
 * string on the tick the first photo goes up. A function takes the id it is
 * given.
 */
export async function uploadSubmissionOption(
  client: SupabaseClient,
  input: {
    submissionId: string;
    componentType: string;
    file: File;
    createdBy: string;
    label?: string;
  },
): Promise<void> {
  const uploaded = await withDriveToken(client, async (token) => {
    const folderId = await ensureProjectFolder(token, 'root', SAMPLE_OPTION_FOLDER);
    const file = await uploadFileToDrive(token, folderId, input.file);
    await shareFileWithLink(token, file.id);
    return file;
  });
  const { error } = await client.from('submission_options').insert({
    submission_id: input.submissionId,
    component_type: input.componentType,
    url: uploaded.webViewLink ?? `https://drive.google.com/file/d/${uploaded.id}/view`,
    label: input.label ?? uploaded.name,
    created_by: input.createdBy,
  });
  if (error) {
    throw new Error(error.message);
  }
}

export function useCreateOption(submissionId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const { access } = useAuth();
  const createdBy = access.status === 'approved' ? access.profile.id : '';
  return useMutation({
    mutationFn: (input: { componentType: string; file: File; label?: string }) =>
      uploadSubmissionOption(client, { ...input, submissionId, createdBy }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sampleKeys.options(submissionId) });
    },
  });
}

/**
 * Send everything staged on this submission to TAP, in one batch.
 *
 * Kyu: "Supplier should send them in batches, once they submitted it they
 * would have to wait til we review before they can submit new versions on the
 * same job order." Per submission rather than per component, because that is
 * the unit he named — a factory photographs the whole job in one session.
 */
export function useSubmitOptions(submissionId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data, error } = await client.rpc('supplier_submit_options', {
        p_submission: submissionId,
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'number' ? data : 0;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sampleKeys.options(submissionId) });
    },
  });
}

export function useRenameSubmission() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { submissionId: string; title: string }) => {
      const { error } = await client.rpc('rename_submission', {
        p_submission: input.submissionId,
        p_title: input.title,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: exchangeKeys.all });
    },
  });
}

export function useTagClient() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { submissionId: string; companyId: string; taggedBy: string }) => {
      const { error } = await client.from('submission_internal_tags').upsert(
        {
          submission_id: input.submissionId,
          company_id: input.companyId,
          tagged_by: input.taggedBy,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'submission_id' },
      );
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: sampleKeys.clients() });
    },
  });
}

export function reviewErrorMessage(raw: string): string {
  if (raw.includes('submission_options_one_winner')) {
    return 'Another option in this component is already approved. Set it back to pending first.';
  }
  if (raw.includes('too_many_options_for_component')) {
    return 'That component already holds five options for this round.';
  }
  if (raw.includes('component_already_approved')) {
    return 'That component is already approved and locked. Nothing more is needed for it.';
  }
  // The batch rules (20261119000200).
  if (raw.includes('batch_already_sent')) {
    return 'These samples are already with TAP. You can add more once they have reviewed this batch.';
  }
  if (raw.includes('nothing_to_submit')) {
    return 'There is nothing waiting to be sent — everything here has already gone to TAP.';
  }
  if (raw.includes('option_not_submitted')) {
    return 'The supplier has not sent this option yet, so it cannot be decided. It is still a draft on their side.';
  }
  if (raw.includes('samples_not_all_approved')) {
    const missing = raw.split('samples_not_all_approved:')[1]?.trim() ?? '';
    return missing === ''
      ? 'Every component needs an approved option before the run can be approved.'
      : `Still to choose: ${missing}. The job only moves to production once every component has an approved option.`;
  }
  if (raw.includes('samples_not_all_submitted')) {
    return 'The supplier still has options they have not sent. Wait for the batch before approving the run.';
  }
  if (raw.includes('not_your_submission')) {
    return 'That submission belongs to another organisation.';
  }
  if (raw.includes('submission_not_open')) {
    return 'This submission is closed. TAP has to send it back before more samples can be added.';
  }
  if (raw.includes('not_authorized_to_rename_submission')) {
    return 'Only the TAP team can rename a submission.';
  }
  if (raw.includes('submission_title_required')) {
    return 'A submission needs a title.';
  }
  if (raw.includes('row-level security') || raw.includes('violates row-level')) {
    return 'You do not have access to change that.';
  }
  // The upload never reached the database: Google would not give us a token.
  // This was landing on the generic message below, which sent people looking
  // for a permissions problem that was not there.
  if (raw.includes('drive_reconnect_required')) {
    return 'Your Google Drive connection has expired. Reconnect Drive from the account menu, then upload again.';
  }
  return 'That change could not be saved. Try again.';
}
